import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";
import {
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Sparkles, IndianRupee,
  Wallet, PiggyBank, LineChart as LineIcon, Plus, X, Trash2, Clock,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Helpers ----
const INR = (n) => {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN")}`;
};
const INR_FULL = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

// Count-up hook
function useCountUp(target, duration = 800) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = 0; const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(start + (target - start) * eased);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return val;
}

const PERIODS = [
  { key: "7d", label: "Last 7 Days" },
  { key: "month", label: "This Month" },
  { key: "quarter", label: "This Quarter" },
  { key: "year", label: "This Year" },
  { key: "custom", label: "Custom" },
];

const GRANS = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Yearly" },
];

const CAT_COLORS = ["#2E7D88", "#F3C55A", "#22C55E", "#4F9CF9", "#8B5CF6", "#F59E0B", "#94A3B8"];

// ==============================================================
export default function Finance() {
  const [period, setPeriod] = useState("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [granularity, setGranularity] = useState("monthly");

  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [cash, setCash] = useState(null);
  const [top, setTop] = useState([]);
  const [activity, setActivity] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddExpense, setShowAddExpense] = useState(false);

  const params = useMemo(() => {
    const p = { period };
    if (period === "custom" && customStart && customEnd) {
      p.start = customStart; p.end = customEnd;
    }
    return p;
  }, [period, customStart, customEnd]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t, b, c, tp, a, i] = await Promise.all([
        axios.get(`${API}/finance/summary`, { params, withCredentials: true }),
        axios.get(`${API}/finance/trend`, { params: { ...params, granularity }, withCredentials: true }),
        axios.get(`${API}/finance/breakdown`, { params, withCredentials: true }),
        axios.get(`${API}/finance/cashflow`, { params, withCredentials: true }),
        axios.get(`${API}/finance/top-expenses`, { params, withCredentials: true }),
        axios.get(`${API}/finance/activity`, { params: { limit: 12 }, withCredentials: true }),
        axios.get(`${API}/finance/insights`, { params, withCredentials: true }),
      ]);
      setSummary(s.data);
      setTrend(t.data);
      setBreakdown(b.data.breakdown || []);
      setCash(c.data);
      setTop(tp.data.top || []);
      setActivity(a.data.activity || []);
      setInsights(i.data.insights || []);
    } catch (e) {
      toast.error("Could not load financials.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, granularity, customStart, customEnd]);

  return (
    <div className="min-h-screen bg-nexa-paper">
      <Sidebar />
      <main className="nexa-compact lg:ml-[260px] min-h-screen">
        <div className="w-full px-3 md:px-5 py-3 md:py-4">

          {/* Header + Filter bar */}
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-3 mb-4">
            <div>
              <div className="lux-eyebrow" data-testid="fin-eyebrow">Executive Financials</div>
              <h1 className="mt-1 text-[15px] md:text-[16px] font-semibold tracking-[-0.02em] text-nexa-ink">
                Expense &amp; <span className="italic">Profitability</span>.
              </h1>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  data-testid={`fin-period-${p.key}`}
                  className={`h-6 px-2.5 rounded-full text-[9px] border transition-colors ${
                    period === p.key ? "bg-nexa-accent text-white border-nexa-ink" : "bg-nexa-card text-nexa-mute border-nexa-line hover:text-nexa-ink"
                  }`}
                >{p.label}</button>
              ))}
              <button data-testid="fin-add-expense" onClick={() => setShowAddExpense(true)} className="lux-btn-accent">
                <Plus className="w-3 h-3 mr-1" strokeWidth={2} /> Add expense
              </button>
            </div>
          </div>

          {period === "custom" && (
            <div className="flex items-center gap-2 mb-3 lux-card p-2.5">
              <span className="text-[9px] uppercase tracking-[0.14em] text-nexa-mute font-semibold">From</span>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                data-testid="fin-custom-start"
                className="lux-input !h-6 !text-[10px]" />
              <span className="text-[9px] uppercase tracking-[0.14em] text-nexa-mute font-semibold">To</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                data-testid="fin-custom-end"
                className="lux-input !h-6 !text-[10px]" />
            </div>
          )}

          {/* HERO */}
          <HeroSection summary={summary} loading={loading} />

          {/* SECTION 1: KPI cards */}
          <SectionHead eyebrow="Financial snapshot" title="Where you stand." />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-5">
            <KPI icon={TrendingUp} label="Revenue" value={INR(summary?.revenue?.value)} delta={summary?.revenue?.delta_pct} tone="emerald" />
            <KPI icon={Wallet} label="Expenses" value={INR(summary?.expenses?.value)} delta={summary?.expenses?.delta_pct} tone="rose" invert />
            <KPI icon={PiggyBank} label="Net Profit" value={INR(summary?.net_profit?.value)} delta={summary?.net_profit?.delta_pct} tone="teal" />
            <KPI icon={LineIcon} label="Profit margin" value={`${summary?.profit_margin?.value || 0}%`} delta={summary?.profit_margin?.delta_pct} tone="amber" isPP />
          </div>

          {/* SECTION 2: Trend chart */}
          <SectionHead
            eyebrow="Revenue vs Expense"
            title="Trend."
            right={
              <div className="flex items-center gap-1">
                {GRANS.map((g) => (
                  <button
                    key={g.key}
                    data-testid={`fin-gran-${g.key}`}
                    onClick={() => setGranularity(g.key)}
                    className={`h-6 px-2 rounded-full text-[9px] border transition-colors ${
                      granularity === g.key ? "bg-nexa-accent text-white border-nexa-ink" : "bg-nexa-card text-nexa-mute border-nexa-line hover:text-nexa-ink"
                    }`}
                  >{g.label}</button>
                ))}
              </div>
            }
          />
          <TrendChart data={trend?.series} />

          {/* SECTION 3 + 4: Breakdown + Cash flow */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
            <div>
              <SectionHead eyebrow="Expense breakdown" title="By category." />
              <BreakdownCard rows={breakdown} />
            </div>
            <div>
              <SectionHead eyebrow="Cash flow" title="In vs out." />
              <CashFlowCard cash={cash} />
            </div>
          </div>

          {/* SECTION 6: Top expenses */}
          <SectionHead eyebrow="Top expenses" title="Highest spend." />
          <TopExpensesCard rows={top} />

          {/* SECTION 8: AI insights */}
          <SectionHead eyebrow="Nexa's take" title="What to watch." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mb-5">
            {insights.map((ins, i) => <InsightCard key={i} ins={ins} i={i} />)}
          </div>

          {/* SECTION 7: Activity */}
          <SectionHead eyebrow="Recent activity" title="What just moved." />
          <ActivityTimeline items={activity} />

        </div>
      </main>

      <AnimatePresence>
        {showAddExpense && (
          <AddExpenseModal
            onClose={() => setShowAddExpense(false)}
            onSaved={() => { setShowAddExpense(false); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ==============================================================
// HERO SECTION
function HeroSection({ summary, loading }) {
  const rev = summary?.revenue?.value || 0;
  const exp = summary?.expenses?.value || 0;
  const net = summary?.net_profit?.value || 0;
  const margin = summary?.profit_margin?.value || 0;
  const cash = summary?.cash_flow?.label || "—";

  const revC = useCountUp(rev);
  const expC = useCountUp(exp);
  const netC = useCountUp(net);

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="relative overflow-hidden rounded-[18px] px-4 md:px-6 py-5 mb-5 border border-white/10"
     style={{
          background: `
            radial-gradient(circle at 18% 18%, rgba(45,200,170,.16), transparent 42%),
            radial-gradient(circle at 90% 15%, rgba(243,197,90,.10), transparent 35%),
            linear-gradient(
              180deg,
              #0F5C58 0%,
              #0C4E4B 55%,
              #093B39 100%
            )
          `,
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,.06), 0 18px 40px rgba(0,0,0,.22)",
        }}
      data-testid="fin-hero"
    >
      <div className="flex items-center gap-2 text-[7.5px] uppercase tracking-[0.22em] text-[#F3C55A]">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#F3C55A", boxShadow: "0 0 8px rgba(243,197,90,.7)" }} />
        ✦ Executive financial summary
      </div>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-4">
        <HeroStat label="Revenue" value={INR_FULL(revC)} tone="white" testId="hero-rev" />
        <HeroStat label="Expenses" value={INR_FULL(expC)} tone="white" testId="hero-exp" />
        <HeroStat label="Net profit" value={INR_FULL(netC)} tone={net >= 0 ? "gold" : "danger"} testId="hero-net" />
        <HeroStat label="Profit margin" value={`${margin}%`} tone="white" testId="hero-margin" />
        <HeroStat label="Cash flow" value={cash} tone={cash === "Healthy" ? "emerald" : cash === "Stable" ? "white" : "danger"} isText testId="hero-cash" />
      </div>
      {loading && <div className="mt-3 text-[9px] text-white/50">Loading current period…</div>}
    </motion.section>
  );
}
function HeroStat({ label, value, tone, isText, testId }) {
  const color = tone === "gold" ? "#F3C55A"
    : tone === "emerald" ? "#6EE7B7"
    : tone === "danger" ? "#FCA5A5"
    : "#FFFFFF";
  return (
    <div>
      <div className="text-[8px] uppercase tracking-[0.2em] text-white/55">{label}</div>
      <div
        className={`mt-1 font-semibold tracking-[-0.02em] ${isText ? "text-[16px]" : "text-[22px]"}`}
        style={{ color, textShadow: tone === "gold" ? "0 0 14px rgba(243,197,90,.25)" : "none" }}
        data-testid={testId}
      >{value}</div>
    </div>
  );
}

// ==============================================================
function SectionHead({ eyebrow, title, right }) {
  return (
    <div className="flex items-end justify-between mb-2">
      <div>
        <div className="lux-eyebrow">{eyebrow}</div>
        <h2 className="mt-0.5 text-[12px] font-semibold tracking-[-0.01em] text-nexa-ink">{title}</h2>
      </div>
      {right}
    </div>
  );
}

// ==============================================================
// KPI card
function KPI({ icon: Icon, label, value, delta, tone, invert, isPP }) {
  const up = (delta || 0) > 0;
  const arrow = up ? ArrowUpRight : ArrowDownRight;
  const Arrow = arrow;
  // If invert=true, positive delta is a bad thing (e.g. expenses up = bad)
  const isGood = invert ? !up : up;
  const chipCls = delta == null ? "text-nexa-mute"
    : isGood ? "text-emerald-600 bg-emerald-50 border-emerald-100"
    : "text-rose-600 bg-rose-50 border-rose-100";
  const tint = {
    emerald: "text-emerald-500 bg-emerald-50",
    rose: "text-rose-500 bg-rose-50",
    teal: "text-nexa-accent bg-nexa-accent/10",
    amber: "text-amber-500 bg-amber-50",
  }[tone] || "text-nexa-accent bg-nexa-accent/10";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="lux-card lux-card-hover p-3"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className={`w-6 h-6 rounded-full grid place-items-center ${tint}`}>
          <Icon className="w-3 h-3" strokeWidth={1.8} />
        </div>
        {delta != null && (
          <span className={`inline-flex items-center gap-0.5 h-4 px-1.5 rounded-full text-[8.5px] font-medium border ${chipCls}`}>
            <Arrow className="w-2 h-2" strokeWidth={2} />
            {Math.abs(delta).toFixed(1)}{isPP ? "pp" : "%"}
          </span>
        )}
      </div>
      <div className="text-[8px] uppercase tracking-[0.14em] text-nexa-mute font-semibold">{label}</div>
      <div className="mt-0.5 text-[18px] font-semibold text-nexa-ink tracking-[-0.02em]">{value}</div>
      <div className="text-[9px] text-nexa-mute mt-0.5">vs previous period</div>
    </motion.div>
  );
}

// ==============================================================
// TREND CHART
function TrendChart({ data }) {
  const empty = !data || data.length === 0 || data.every((d) => d.revenue === 0 && d.expenses === 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="lux-card p-4 mb-5"
    >
      {empty ? (
        <div className="h-56 flex flex-col items-center justify-center text-center">
          <div className="w-8 h-8 rounded-full bg-nexa-canvas flex items-center justify-center mb-2">
            <LineIcon className="w-3.5 h-3.5 text-nexa-mute" strokeWidth={1.5} />
          </div>
          <div className="text-[10.5px] text-nexa-ink font-medium">No data yet.</div>
          <div className="text-[9px] text-nexa-mute mt-0.5">Revenue &amp; expenses will appear here as they happen.</div>
        </div>
      ) : (
        <div className="h-56" data-testid="fin-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 6 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#22C55E" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#22C55E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E8EDF3" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#E8EDF3" }} minTickGap={20} />
              <YAxis tick={{ fontSize: 9, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#E8EDF3" }} tickFormatter={(v) => INR(v)} width={44} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #E8EDF3", boxShadow: "0 8px 22px rgba(0,0,0,.06)", fontSize: 10 }}
                formatter={(v, n) => [INR_FULL(v), n[0].toUpperCase() + n.slice(1)]}
                labelStyle={{ color: "#64748B", fontSize: 9 }}
              />
              <Line type="monotone" dataKey="revenue" stroke="#22C55E" strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive animationDuration={800} />
              <Line type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive animationDuration={900} />
              <Line type="monotone" dataKey="profit" stroke="#2E7D88" strokeWidth={2} strokeDasharray="4 3" dot={false} activeDot={{ r: 4 }} isAnimationActive animationDuration={1000} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="mt-2 flex items-center gap-3 text-[9.5px] text-nexa-mute">
        <LegendDot color="#22C55E" label="Revenue" />
        <LegendDot color="#EF4444" label="Expenses" />
        <LegendDot color="#2E7D88" label="Profit" dashed />
      </div>
    </motion.div>
  );
}
function LegendDot({ color, label, dashed }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-3 h-[2px]" style={{ background: color, borderStyle: dashed ? "dashed" : "solid" }} />
      {label}
    </span>
  );
}

// ==============================================================
// BREAKDOWN — horizontal bars
function BreakdownCard({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="lux-card p-4 h-full min-h-[220px] flex flex-col items-center justify-center text-center">
        <div className="text-[10.5px] text-nexa-ink font-medium">No expenses yet.</div>
        <div className="text-[9px] text-nexa-mute mt-0.5">Categories will appear here once invoices or expenses are added.</div>
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="lux-card p-4 space-y-2.5"
      data-testid="fin-breakdown"
    >
      {rows.map((r, i) => (
        <div key={r.category}>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-nexa-ink font-medium flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm" style={{ background: CAT_COLORS[i % CAT_COLORS.length] }} />
              {r.category}
            </span>
            <span className="text-nexa-mute">{INR_FULL(r.amount)} · <span className="text-nexa-ink font-medium">{r.pct}%</span></span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-nexa-canvas overflow-hidden">
            <motion.div
              initial={{ width: 0 }} animate={{ width: `${r.pct}%` }} transition={{ duration: 0.6, delay: 0.05 * i }}
              className="h-full"
              style={{ background: CAT_COLORS[i % CAT_COLORS.length] }}
            />
          </div>
        </div>
      ))}
    </motion.div>
  );
}

// ==============================================================
// CASH FLOW CARD
function CashFlowCard({ cash }) {
  if (!cash) {
    return <div className="lux-card p-4 h-full min-h-[220px]"><div className="lux-skel h-3 w-1/3 mb-2" /><div className="lux-skel h-2 w-2/3" /></div>;
  }
  const total = Math.max(1, cash.incoming + cash.outgoing);
  const inPct = (cash.incoming / total) * 100;
  const outPct = 100 - inPct;
  const chip = cash.status === "Healthy" ? "text-emerald-600 bg-emerald-50 border-emerald-100"
    : cash.status === "Stable" ? "text-nexa-mute bg-nexa-canvas border-nexa-line"
    : "text-rose-600 bg-rose-50 border-rose-100";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="lux-card p-4"
      data-testid="fin-cashflow"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[8px] uppercase tracking-[0.14em] text-nexa-mute font-semibold">Cash flow status</div>
          <div className="text-[15px] font-semibold text-nexa-ink mt-0.5">{cash.status}</div>
        </div>
        <span className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[9px] font-medium border ${chip}`}>
          Score {cash.score}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div>
          <div className="text-[8px] uppercase tracking-[0.14em] text-nexa-mute font-semibold">Incoming</div>
          <div className="text-[13px] font-semibold text-emerald-600 mt-0.5">{INR_FULL(cash.incoming)}</div>
        </div>
        <div>
          <div className="text-[8px] uppercase tracking-[0.14em] text-nexa-mute font-semibold">Outgoing</div>
          <div className="text-[13px] font-semibold text-rose-500 mt-0.5">{INR_FULL(cash.outgoing)}</div>
        </div>
        <div>
          <div className="text-[8px] uppercase tracking-[0.14em] text-nexa-mute font-semibold">Net</div>
          <div className={`text-[13px] font-semibold mt-0.5 ${cash.net >= 0 ? "text-nexa-ink" : "text-rose-500"}`}>{INR_FULL(cash.net)}</div>
        </div>
      </div>

      <div className="h-2 rounded-full overflow-hidden flex bg-nexa-canvas">
        <motion.div initial={{ width: 0 }} animate={{ width: `${inPct}%` }} transition={{ duration: 0.6 }} className="h-full bg-emerald-500" />
        <motion.div initial={{ width: 0 }} animate={{ width: `${outPct}%` }} transition={{ duration: 0.6, delay: 0.1 }} className="h-full bg-rose-400" />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[9px] text-nexa-mute">
        <span>{Math.round(inPct)}% in</span>
        <span>Outstanding {INR_FULL(cash.outstanding)}</span>
        <span>{Math.round(outPct)}% out</span>
      </div>
    </motion.div>
  );
}

// ==============================================================
// TOP EXPENSES (bar chart-style rows)
function TopExpensesCard({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="lux-card p-4 mb-5 text-center">
        <div className="text-[10.5px] text-nexa-ink font-medium">No expenses yet.</div>
        <div className="text-[9px] text-nexa-mute mt-0.5">Once invoices or expenses roll in, top categories will show here.</div>
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="lux-card p-4 mb-5"
      data-testid="fin-top-expenses"
    >
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="#E8EDF3" strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 9, fill: "#64748B" }} tickLine={false} axisLine={false} tickFormatter={(v) => INR(v)} />
            <YAxis dataKey="category" type="category" tick={{ fontSize: 10, fill: "#17212B" }} tickLine={false} axisLine={false} width={110} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: "1px solid #E8EDF3", boxShadow: "0 8px 22px rgba(0,0,0,.06)", fontSize: 10 }}
              formatter={(v) => INR_FULL(v)}
              cursor={{ fill: "rgba(46,125,136,.05)" }}
            />
            <Bar dataKey="amount" radius={[6, 6, 6, 6]} isAnimationActive animationDuration={700}>
              {rows.map((r, i) => (
                <Cell key={r.category} fill={CAT_COLORS[i % CAT_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}

// ==============================================================
// INSIGHT CARD
function InsightCard({ ins, i }) {
  const tone = ins.tone || "info";
  const cls = tone === "positive" ? "border-emerald-100 bg-emerald-50/40"
    : tone === "warning" ? "border-amber-100 bg-amber-50/40"
    : "border-nexa-line";
  const dot = tone === "positive" ? "bg-emerald-500"
    : tone === "warning" ? "bg-amber-500"
    : "bg-nexa-accent";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 * i }}
      className={`lux-card p-3 flex items-start gap-2.5 ${cls}`}
      data-testid={`fin-insight-${i}`}
    >
      <div className="mt-0.5 w-5 h-5 rounded-full grid place-items-center shrink-0 bg-white border border-nexa-line">
        <Sparkles className={`w-2.5 h-2.5 text-nexa-accent`} strokeWidth={1.7} />
      </div>
      <div>
        <div className="flex items-center gap-1.5">
          <span className={`w-1 h-1 rounded-full ${dot}`} />
          <div className="text-[10.5px] text-nexa-ink font-medium leading-tight">{ins.title}</div>
        </div>
        {ins.detail && <div className="text-[9px] text-nexa-mute mt-1 leading-4">{ins.detail}</div>}
      </div>
    </motion.div>
  );
}

// ==============================================================
// ACTIVITY TIMELINE
function ActivityTimeline({ items }) {
  if (!items || items.length === 0) {
    return (
      <div className="lux-card p-4 text-center">
        <div className="mx-auto mb-2 w-7 h-7 rounded-full bg-nexa-canvas flex items-center justify-center">
          <Clock className="w-3 h-3 text-nexa-mute" strokeWidth={1.5} />
        </div>
        <div className="text-[10.5px] text-nexa-ink font-medium">No activity yet.</div>
      </div>
    );
  }
  return (
    <div className="lux-card p-1.5" data-testid="fin-activity">
      {items.map((ev, i) => {
        const inFlow = ev.direction === "in";
        return (
          <div key={i} className={`flex items-center gap-3 px-3 py-2 ${i === items.length - 1 ? "" : "border-b border-nexa-line"}`}>
            <div className={`w-6 h-6 rounded-full border grid place-items-center shrink-0 ${inFlow ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-rose-100 bg-rose-50 text-rose-500"}`}>
              {inFlow ? <ArrowDownRight className="w-2.5 h-2.5" strokeWidth={1.8} /> : <ArrowUpRight className="w-2.5 h-2.5" strokeWidth={1.8} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] text-nexa-ink truncate">{ev.title}</div>
              <div className="text-[9px] text-nexa-mute mt-0.5 truncate">{ev.meta}</div>
            </div>
            <div className="text-[9px] text-nexa-mute whitespace-nowrap">{relTime(ev.at)}</div>
          </div>
        );
      })}
    </div>
  );
}
function relTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

// ==============================================================
// ADD EXPENSE MODAL
const CATEGORIES = ["Office Rent", "Utilities", "Marketing", "Staff", "Software", "Other"];
function AddExpenseModal({ onClose, onSaved }) {
  const [category, setCategory] = useState("Office Rent");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      await axios.post(`${API}/finance/expenses`, {
        category, amount: Number(amount), note: note || null, occurred_at: occurredAt,
      }, { withCredentials: true });
      toast.success("Expense recorded");
      onSaved();
    } catch {
      toast.error("Could not save expense.");
    } finally { setSaving(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-nexa-accent/40 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.form
        initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 8, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="lux-card w-full max-w-lg p-6 relative max-h-[85vh] overflow-y-auto"
        data-testid="fin-expense-modal"
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-nexa-mute hover:text-nexa-ink" aria-label="Close">
          <X className="w-4 h-4" strokeWidth={1.7} />
        </button>
        <div className="lux-eyebrow">Record</div>
        <h2 className="text-[16px] font-semibold tracking-tight text-nexa-ink mt-1 mb-5">Add an expense</h2>

        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2">
            <div className="lux-label">Category</div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="lux-input w-full" data-testid="exp-cat">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label>
            <div className="lux-label">Amount (₹)</div>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="lux-input w-full" data-testid="exp-amount" required />
          </label>
          <label>
            <div className="lux-label">Date</div>
            <input type="date" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} className="lux-input w-full" data-testid="exp-date" />
          </label>
          <label className="col-span-2">
            <div className="lux-label">Note</div>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="lux-input w-full" placeholder="Optional description" data-testid="exp-note" />
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lux-btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="lux-btn-accent" data-testid="exp-save">
            {saving ? "Saving…" : "Save expense"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
